/**
 * Hook for handling API errors with i18n translation and form field mapping.
 *
 * This hook provides utilities for:
 * 1. Translating error codes to user-friendly messages
 * 2. Mapping field errors to React Hook Form
 * 3. Displaying toast notifications with error_id for debugging
 *
 * Usage:
 *   const { handleError, handleMutationError } = useApiError();
 *
 *   // In a mutation's onError callback:
 *   const mutation = useMutation({
 *     mutationFn: createItem,
 *     onError: (error) => handleMutationError(error, form.setError),
 *   });
 *
 *   // Or in a try/catch:
 *   try {
 *     await mutation.mutateAsync(data);
 *   } catch (error) {
 *     handleError(error);
 *   }
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { FieldValues, Path, UseFormSetError } from 'react-hook-form';
import {
  getErrorMessage,
  getFieldErrors,
  getErrorId,
  getErrorCode,
  isValidationError,
} from '../utils/errors';

interface UseApiErrorOptions {
  showToast?: boolean;
  includeErrorId?: boolean;
}

export function useApiError(options: UseApiErrorOptions = {}) {
  const { showToast = true, includeErrorId = true } = options;
  const { t } = useTranslation('errors');

  /**
   * Handle an API error with toast notification.
   * Use this for simple error handling without form field mapping.
   */
  const handleError = useCallback(
    (error: unknown, interpolation?: Record<string, string | number>) => {
      const errorMessage = getErrorMessage(error, interpolation);
      const errorId = getErrorId(error);

      if (showToast) {
        const displayMessage =
          includeErrorId && errorId
            ? `${errorMessage} (${errorId})`
            : errorMessage;
        toast.error(displayMessage);
      }

      return { message: errorMessage, errorId, code: getErrorCode(error) };
    },
    [showToast, includeErrorId]
  );

  /**
   * Handle a mutation error with form field mapping.
   * Automatically maps field errors to React Hook Form fields and shows toast for non-field errors.
   */
  const handleMutationError = useCallback(
    <T extends FieldValues>(
      error: unknown,
      setError?: UseFormSetError<T>,
      interpolation?: Record<string, string | number>
    ) => {
      const errorId = getErrorId(error);

      // Check for field-level errors (both validation errors and single-field errors like DUPLICATE_DOMAIN)
      if (setError) {
        const fieldErrors = getFieldErrors(error);
        if (fieldErrors) {
          let hasSetFieldError = false;
          for (const [field, fieldMessage] of Object.entries(fieldErrors)) {
            setError(field as Path<T>, {
              type: 'server',
              message: fieldMessage,
            });
            hasSetFieldError = true;
          }
          // Only show toast if no field errors were set
          if (!hasSetFieldError && showToast) {
            const errorMessage = getErrorMessage(error, interpolation);
            toast.error(
              includeErrorId && errorId
                ? `${errorMessage} (${errorId})`
                : errorMessage
            );
          }
          return { fieldErrors, errorId, code: getErrorCode(error) };
        }
      }

      // Non-field error - show toast
      return handleError(error, interpolation);
    },
    [handleError, showToast, includeErrorId]
  );

  /**
   * Get translated error message without side effects.
   * Useful when you need to display the error in a custom way.
   */
  const getTranslatedError = useCallback(
    (error: unknown, interpolation?: Record<string, string | number>) => {
      return {
        message: getErrorMessage(error, interpolation),
        errorId: getErrorId(error),
        code: getErrorCode(error),
        fieldErrors: getFieldErrors(error),
        isValidation: isValidationError(error),
      };
    },
    []
  );

  return {
    handleError,
    handleMutationError,
    getTranslatedError,
    t, // Expose t for custom error messages
  };
}

export default useApiError;
